// Script loader utility for loading external ad scripts

export interface ScriptLoadOptions {
  src: string;
  id?: string;
  async?: boolean;
  defer?: boolean;
  onLoad?: () => void;
  onError?: (error: Error) => void;
}

// Cache for loaded scripts to prevent duplicate loading
const loadedScripts = new Set<string>();
const loadingScripts = new Map<string, Promise<void>>();

/**
 * Dynamically load a script and return a promise
 */
export const loadScript = (options: ScriptLoadOptions): Promise<void> => {
  const { src, id, async = true, defer = false, onLoad, onError } = options;
  
  // Use src as key if no id provided
  const scriptKey = id || src;
  
  // Return immediately if script is already loaded
  if (loadedScripts.has(scriptKey)) {
    return Promise.resolve();
  }
  
  // Return existing promise if script is currently loading
  if (loadingScripts.has(scriptKey)) {
    return loadingScripts.get(scriptKey)!;
  }
  
  // Create new loading promise
  const loadingPromise = new Promise<void>((resolve, reject) => {
    // Check if script already exists in DOM
    const existingScript = document.getElementById(scriptKey);
    if (existingScript) {
      loadedScripts.add(scriptKey);
      resolve();
      return;
    }
    
    // Create script element
    const script = document.createElement('script');
    script.src = src;
    script.async = async;
    script.defer = defer;
    
    if (id) {
      script.id = id;
    }
    
    // Handle successful load
    script.onload = () => {
      loadedScripts.add(scriptKey);
      loadingScripts.delete(scriptKey);
      onLoad?.();
      resolve();
    };
    
    // Handle load error
    script.onerror = (event) => {
      loadingScripts.delete(scriptKey);
      const error = new Error(`Failed to load script: ${src}`);
      onError?.(error);
      reject(error);
    };
    
    // Add script to document head
    document.head.appendChild(script);
  });
  
  // Cache the loading promise
  loadingScripts.set(scriptKey, loadingPromise);
  
  return loadingPromise;
};

/**
 * Load Prebid.js script
 */
export const loadPrebidScript = (): Promise<void> => {
  return loadScript({
    src: 'https://cdn.jsdelivr.net/npm/prebid.js@latest/dist/not-for-prod/prebid.js',
    id: 'prebid-script',
    async: true,
    onLoad: () => {
      console.log('Prebid.js loaded successfully');
      
      // Initialize Prebid queue if not already present
      if (!window.pbjs) {
        (window as any).pbjs = { que: [] };
      }
    },
    onError: (error) => {
      console.error('Failed to load Prebid.js:', error);
    },
  });
};

/**
 * Load Google Ad Manager (GPT) script
 */
export const loadGoogleAdManagerScript = (): Promise<void> => {
  return loadScript({
    src: 'https://securepubads.g.doubleclick.net/tag/js/gpt.js',
    id: 'gpt-script',
    async: true,
    onLoad: () => {
      console.log('Google Ad Manager loaded successfully');
      
      // Initialize googletag queue if not already present
      if (!window.googletag) {
        (window as any).googletag = { cmd: [] };
      }
    },
    onError: (error) => {
      console.error('Failed to load Google Ad Manager:', error);
    },
  });
};

/**
 * Load all required ad scripts in parallel
 */
export const loadAdScripts = async (): Promise<void> => {
  try {
    console.log('Loading ad scripts...');
    
    // Load both scripts in parallel
    await Promise.all([
      loadPrebidScript(),
      loadGoogleAdManagerScript(),
    ]);
    
    console.log('All ad scripts loaded successfully');
  } catch (error) {
    console.error('Failed to load ad scripts:', error);
    throw error;
  }
};

/**
 * Check if a script is loaded
 */
export const isScriptLoaded = (scriptKey: string): boolean => {
  return loadedScripts.has(scriptKey);
};

/**
 * Check if Prebid.js is ready
 */
export const isPrebidReady = (): boolean => {
  return typeof window.pbjs !== 'undefined' && Array.isArray(window.pbjs.que);
};

/**
 * Check if Google Ad Manager is ready
 */
export const isGoogleAdManagerReady = (): boolean => {
  return typeof window.googletag !== 'undefined' && Array.isArray(window.googletag.cmd);
};

/**
 * Wait for Prebid.js to be ready
 */
export const waitForPrebid = (timeout = 5000): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (isPrebidReady()) {
      resolve();
      return;
    }
    
    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (isPrebidReady()) {
        clearInterval(checkInterval);
        resolve();
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval);
        reject(new Error('Timeout waiting for Prebid.js to be ready'));
      }
    }, 100);
  });
};

/**
 * Wait for Google Ad Manager to be ready
 */
export const waitForGoogleAdManager = (timeout = 5000): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (isGoogleAdManagerReady()) {
      resolve();
      return;
    }
    
    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (isGoogleAdManagerReady()) {
        clearInterval(checkInterval);
        resolve();
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval);
        reject(new Error('Timeout waiting for Google Ad Manager to be ready'));
      }
    }, 100);
  });
};

/**
 * Remove a loaded script from the DOM and cache
 */
export const removeScript = (scriptKey: string): void => {
  const script = document.getElementById(scriptKey);
  if (script) {
    script.remove();
  }
  
  loadedScripts.delete(scriptKey);
  loadingScripts.delete(scriptKey);
};

/**
 * Clean up all loaded ad scripts
 */
export const cleanupAdScripts = (): void => {
  removeScript('prebid-script');
  removeScript('gpt-script');
  
  // Clean up global variables
  if (window.pbjs) {
    delete (window as any).pbjs;
  }
  
  if (window.googletag) {
    delete (window as any).googletag;
  }
  
  console.log('Ad scripts cleaned up');
};